import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogIn, Bell, Settings } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import type { PlaylistItem } from '@/shared/types';
import { MobileSheet } from '@/shared/components/MobileSheet';
import { useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import { formatPhoneBrMask } from '@/shared/lib/phoneUtils';
import { getClientePrimeiroNome } from '../../domain/usecases/clienteDisplay';

interface Props {
  item:    PlaylistItem;
  posicao: number;
  etaLabel?: string | null;
  onDismiss: () => void;
}

export function PedidoConfirmado({ item, posicao, etaLabel, onDismiss }: Props) {
  const { telaId } = useParams<{ telaId: string }>();
  const { status, cliente, openLogin } = useJukeboxCliente();

  useEffect(() => {
    const t = setTimeout(onDismiss, 6_000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const phoneDisplay = cliente?.phone_e164
    ? formatPhoneBrMask(cliente.phone_e164.replace(/^55/, ''))
    : '';
  const primeiroNome = getClientePrimeiroNome(cliente?.nome);

  return (
    <MobileSheet aberto titulo="Pedido confirmado" onClose={onDismiss}>
      <div className="overflow-hidden">
        <div className="relative h-32 mx-4 mt-2 rounded-2xl overflow-hidden">
          <img src={item.thumbnail} alt={item.titulo} className="w-full h-full object-cover" />
          <div className="absolute inset-0 pointer-events-none">
            <motion.div
              className="absolute left-0 -top-2 -bottom-2 w-1/3 bg-gradient-to-r from-transparent via-white/[0.10] to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '200%' }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-white font-bold text-sm leading-snug line-clamp-2">{item.titulo}</p>
            {item.canal && <p className="text-white/55 text-xs mt-0.5">{item.canal}</p>}
          </div>
        </div>

        <div className="flex items-center gap-4 px-4 py-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center shrink-0 animate-scale-in">
            <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">
              {primeiroNome ? `Pedido confirmado, ${primeiroNome}!` : 'Pedido confirmado!'}
            </p>
            <p className="text-white/55 text-xs mt-0.5">
              {posicao === 1
                ? (etaLabel
                  ? `Você é o próximo na fila — ${etaLabel}`
                  : 'Você é o próximo na fila')
                : (etaLabel
                  ? `Posição ${posicao} na fila — ${etaLabel}`
                  : `Posição ${posicao} na fila — em breve tocará na TV`)
              }
            </p>
          </div>
        </div>

        {posicao > 1 && (
          <div className="px-4 pb-2">
            <div className="flex gap-1">
              {Array.from({ length: Math.min(posicao, 8) }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full animate-fade-in-up ${
                    i === posicao - 1 ? 'bg-orange-500' : 'bg-white/10'
                  }`}
                  style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'both' }}
                />
              ))}
            </div>
            <p className="text-white/50 text-[10px] mt-1 text-right">{posicao} na fila</p>
          </div>
        )}

        {status === 'anonymous' ? (
          <div className="px-4 pb-3">
            <button
              onClick={() => openLogin()}
              className="w-full flex items-center gap-2.5 p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
            >
              <LogIn size={16} className="text-emerald-400 shrink-0" />
              <span className="text-xs text-white/50">
                <span className="text-emerald-400 font-medium">Faça login</span> para receber avisos no WhatsApp
              </span>
            </button>
          </div>
        ) : (
          <div className="px-4 pb-3">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/10">
              <Bell size={16} className="text-emerald-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-emerald-400 font-medium">Avisos no WhatsApp ativos</p>
                {phoneDisplay && (
                  <p className="text-[11px] text-white/55 mt-0.5">{phoneDisplay}</p>
                )}
                {telaId && (
                  <Link
                    to={`/jukebox/${telaId}/perfil`}
                    className="inline-flex items-center gap-1 text-[11px] text-white/55 hover:text-white/80 mt-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 rounded"
                  >
                    <Settings size={11} />
                    Gerenciar no Perfil
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="px-4 pb-6 pt-1">
          <button
            onClick={onDismiss}
            className="w-full py-3.5 rounded-2xl bg-white/[0.07] text-white text-sm font-semibold active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-transform"
          >
            Ok, entendi!
          </button>
        </div>
      </div>
    </MobileSheet>
  );
}
